import { computed, type ComputedRef } from "vue";

import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";

// Type-only, and from the concrete module rather than the feature barrel: the barrel pulls the
// wizard's components (and through them echarts and the 3D viewer) into every consumer's graph.
import type { TransportTypesType } from "@/features/BuildingProject/types/TransportTypesType";

/** Which revision the rows came from — for tests and for reading a debug log. */
export type TransportTypesSource = "revision" | "working";

export interface TransportTypesForRead {
  /**
   * The transport types of the revision being READ. `undefined` only while the working state has not
   * loaded yet, which callers must keep telling apart from "this revision has none".
   */
  transportTypes: ComputedRef<TransportTypesType[] | undefined>;
  source: ComputedRef<TransportTypesSource>;
  /** One row by its `project_transport.id`, within the revision being read. */
  transportTypeById: (transportTypeId: string | undefined) => TransportTypesType | undefined;
  /**
   * A watch key that moves when the SET moves — ids and names — and not when an equal set arrives in
   * a new array. A background wrapper refresh or a revisions reload replaces the array object with
   * equal contents, and watching the array itself made that rebuild (and thereby clear) the filters.
   */
  transportTypesKey: ComputedRef<string>;
}

/**
 * The transport TYPES that belong to the revision the page is reading.
 *
 * The Transport mapping page reads its mapping rows pinned: `TransportMappingListProvider` filters
 * them on `readPinRevisionIdFor`, and the REST inventory and summary take the same pin. The TYPES
 * those rows name were still read from the working state, and under a preview that can never match —
 * `project_revision_save` archives a frozen revision's rows under deterministic new ids
 * (`md5(revision_id || row_id)`) and remaps each mapping's `project_transport_id` to the copy, so a
 * frozen mapping row names a transport row the working list does not contain. Entries rendered
 * blank, their emission counted as zero, the type filter listed the wrong revision's names, and the
 * "no transport types" gate answered from today's count (RTLCA-5772).
 *
 * So: one source, keyed on the SAME getter the mapping query filters on, so the entries and the
 * types they name can never come from two different revisions.
 *
 * What deliberately does NOT use this: the Add-mapping form (`MappingListItemNew`,
 * `TransportFormGeneral`). An Add is a write, and a write lands on the working revision — the
 * trigger stamps it there — so its option list and its default must be the WORKING rows. Preview
 * leaves those fields live and blocks only the Add itself (RTLCA-5254).
 */
export const useTransportTypesForRead = (): TransportTypesForRead => {
  const projectWrapper = useProjectWrapper();
  const revisionsStore = useProjectRevisionsStore();

  const projectId = computed(() => String(projectWrapper.getProjectWrapper().project?.id ?? ""));

  const resolved = computed<{ source: TransportTypesSource; rows: TransportTypesType[] | undefined }>(() => {
    const id = projectId.value;
    const pin = id ? revisionsStore.readPinRevisionIdFor(id) : null;

    if (pin) {
      // Under a pin the answer is that revision's own rows or NOTHING — never the working rows,
      // which is the whole point. `?? []` is a floor, not a fallback: it covers a revision whose
      // rows were not loaded, and answering "none" there is still better than answering with
      // another revision's.
      return { source: "revision", rows: revisionsStore.readTransportsFor(id) ?? [] };
    }

    // Working state. `undefined` is preserved rather than coerced to `[]`: the requirements gate
    // reads a definite empty list as "this project has no transport types" and sends the user to
    // Project Settings, which is the wrong thing to say before the project has loaded.
    return { source: "working", rows: projectWrapper.getTransportTypes() };
  });

  const transportTypes = computed(() => resolved.value.rows);
  const source = computed(() => resolved.value.source);

  const transportTypesKey = computed(() =>
    (resolved.value.rows ?? []).map((row) => `${row.id}:${row.transport_type_name ?? ""}`).join("|")
  );

  const transportTypeById = (transportTypeId: string | undefined) =>
    transportTypeId ? resolved.value.rows?.find((row) => row.id === transportTypeId) : undefined;

  return { transportTypes, source, transportTypeById, transportTypesKey };
};

export default useTransportTypesForRead;
